import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { DataTable } from "@/components/ui/data-table";
import { PoultrySearch } from "./PoultrySearch";
import { getPoultryColumns } from "./PoultryColumns";
import { useEffect, useState } from "react";
import { PoultryItem } from "./types";
import { TablePagination } from "@/components/ui/data-table/TablePagination";
import { StockItemData } from "@/types/stock";
import { RestockDialog } from "./dialogs/RestockDialog";
import { useRestockManagement } from "@/hooks/poultry/useRestockManagement";
import { Button } from "@/components/ui/button";
import { HelpCircle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
interface PoultryInventoryProps {
  onLossClick: (item: PoultryItem) => void;
  onReturnClick: (item: PoultryItem) => void;
}
export const PoultryInventory = ({
  onLossClick,
  onReturnClick
}: PoultryInventoryProps) => {
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const {
    isRestockDialogOpen,
    setIsRestockDialogOpen,
    selectedItem,
    restockQuantity,
    setRestockQuantity,
    handleRestockClick,
    handleRestockConfirm
  } = useRestockManagement();
  const {
    data: poultryItems,
    isLoading
  } = useQuery({
    queryKey: ['poultryInventory'],
    queryFn: async () => {
      const {
        data: stockItems,
        error
      } = await supabase.from('stock_items').select('*').order('created_at', {
        ascending: false
      });
      if (error) throw error;
      return (stockItems || []).map(item => ({
        id: item.id,
        data: item.data as StockItemData,
        quantity: item.quantity || 0
      }));
    }
  });
  useEffect(() => {
    const channel = supabase.channel('stock-updates').on('postgres_changes', {
      event: '*',
      schema: 'public',
      table: 'stock_items'
    }, () => {
      queryClient.invalidateQueries({
        queryKey: ['poultryInventory']
      });
    }).subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [queryClient]);
  const columns = getPoultryColumns(onLossClick, onReturnClick, handleRestockClick);
  const filteredItems = poultryItems?.filter(item => {
    const searchLower = searchQuery.toLowerCase();
    return !searchQuery || item.data.Race?.toLowerCase().includes(searchLower) || item.data.Age?.toLowerCase().includes(searchLower) || item.data.Santé?.toLowerCase().includes(searchLower);
  });
  const totalItems = filteredItems?.length || 0;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedItems = filteredItems?.slice(startIndex, startIndex + itemsPerPage);
  if (isLoading) {
    return <div>Chargement de l'inventaire...</div>;
  }
  return <Card>
      <CardContent>
        <div className="flex justify-between items-center mb-4">
          <PoultrySearch searchQuery={searchQuery} setSearchQuery={setSearchQuery} />
          <Dialog>
            <DialogTrigger asChild>
              <Button variant="ghost" className="flex items-center gap-2">
                <HelpCircle className="h-5 w-5" />
                <span className="font-normal text-base text-amber-600">Comment ça marche ?</span>
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[80vh] p-0">
              <DialogHeader className="p-6 pb-0">
                <DialogTitle>Guide de la Ferme Avicole</DialogTitle>
              </DialogHeader>
              <ScrollArea className="px-6 pb-6" style={{
              maxHeight: "calc(80vh - 60px)"
            }}>
                <div className="space-y-4">
                  <div>
                    <h3 className="font-semibold mb-2">Gestion des Stocks</h3>
                    <p className="text-sm text-muted-foreground">
                      Suivez et gérez votre inventaire de volailles en temps réel. Vous pouvez voir la race, l'âge, la santé et la quantité de chaque lot.
                    </p>
                  </div>
                  
                  <div>
                    <h3 className="font-semibold mb-2">Actions Disponibles</h3>
                    <ul className="space-y-2 text-sm text-muted-foreground">
                      <li>• <strong>Réapprovisionnement :</strong> Ajoutez des volailles à un lot existant en utilisant le bouton "Réapprovisionner". Cette action met à jour automatiquement les stocks et l'historique.</li>
                      <li>• <strong>Gestion des Pertes :</strong> Enregistrez les pertes de volailles avec le bouton "Perte". Important pour le suivi de la mortalité et les rapports financiers.</li>
                      <li>• <strong>Retours :</strong> Gérez les retours clients avec le bouton "Retour". Les retours sont automatiquement reflétés dans l'inventaire.</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="font-semibold mb-2">Liens avec les Autres Modules</h3>
                    <ul className="space-y-2 text-sm text-muted-foreground">
                      <li>• <strong>Historique :</strong> Consultez l'historique complet des transactions dans l'onglet "Historique". Chaque mouvement de stock y est enregistré.</li>
                      <li>• <strong>Production d'Œufs :</strong> Suivez la production journalière d'œufs dans l'onglet dédié. Les statistiques sont liées à vos lots de volailles.</li>
                      <li>• <strong>Budget et Finances :</strong> Les transactions de stock impactent directement vos rapports financiers dans l'onglet "Budget".</li>
                      <li>• <strong>Transactions :</strong> Toutes les ventes, pertes et retours sont automatiquement comptabilisés et visibles dans les rapports financiers.</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="font-semibold mb-2">Conseils d'Utilisation</h3>
                    <ul className="space-y-2 text-sm text-muted-foreground">
                      <li>• Utilisez la barre de recherche pour trouver rapidement un lot spécifique</li>
                      <li>• Vérifiez régulièrement l'état de santé des lots</li>
                      <li>• Maintenez à jour les informations sur l'âge des volailles</li>
                      <li>• Consultez les rapports de production pour optimiser votre gestion</li>
                    </ul>
                  </div>
                </div>
              </ScrollArea>
            </DialogContent>
          </Dialog>
        </div>

        <DataTable columns={columns} data={paginatedItems || []} />
        {totalPages > 1 && <TablePagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />}
        {selectedItem && <RestockDialog open={isRestockDialogOpen} onOpenChange={setIsRestockDialogOpen} selectedItem={selectedItem} restockQuantity={restockQuantity} onQuantityChange={setRestockQuantity} onConfirm={handleRestockConfirm} />}
      </CardContent>
    </Card>;
};